import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
  deleteField,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "@/lib/firebase";
import type { DsaPartner, DsaPartnerType, PartnerDocument } from "@/types";

const dsaPartnersCollection = collection(db, "dsaPartners");

export interface DsaChangeHistoryEntry {
  id?: string;
  changedAt: Date;
  changedBy: string;
  changes: { field: string; oldValue: unknown; newValue: unknown }[];
}

export const addDsaChangeHistoryEntry = async (
  partnerId: string,
  changedBy: string,
  changes: { field: string; oldValue: unknown; newValue: unknown }[],
): Promise<void> => {
  if (!changes.length) return;

  try {
    const historyCollection = collection(
      db,
      "dsaPartners",
      partnerId,
      "changeHistory",
    );
    await addDoc(historyCollection, {
      changedAt: Timestamp.now(),
      changedBy,
      changes: changes.map((c) => ({
        field: c.field,
        oldValue: c.oldValue ?? null,
        newValue: c.newValue ?? null,
      })),
    });
  } catch (error) {
    console.error("Error adding DSA change history entry:", error);
  }
};

export const uploadDsaPartnerDocument = async (
  file: File,
  userId: string,
  partnerId?: string,
): Promise<PartnerDocument> => {
  try {
    const timestamp = Date.now();
    const uniqueFilename = `${timestamp}-${file.name}`;
    const storagePath = `dsaPartners/${userId}/${partnerId || "temp"}/${uniqueFilename}`;
    const storageRef = ref(storage, storagePath);
    const uploadResult = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(uploadResult.ref);

    return {
      id: `doc_${timestamp}_${Math.random().toString(36).substring(7)}`,
      name: file.name,
      type: file.type,
      size: file.size,
      url: downloadURL,
    };
  } catch (error) {
    console.error("Error uploading DSA partner document:", error);
    throw new Error(
      `Failed to upload document: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
};

export const deleteDsaPartnerDocument = async (
  fileUrl: string,
): Promise<void> => {
  try {
    if (!fileUrl || fileUrl.startsWith("data:")) {
      return;
    }

    const url = new URL(fileUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)$/);

    if (!pathMatch) {
      console.warn("Could not extract storage path from URL:", fileUrl);
      return;
    }

    const storagePath = decodeURIComponent(pathMatch[1]);
    const fileRef = ref(storage, storagePath);
    await deleteObject(fileRef);
  } catch (error) {
    console.error("Error deleting DSA file from storage:", error);
  }
};

export const deleteDsaPartnerDocuments = async (
  documents: PartnerDocument[],
): Promise<void> => {
  if (!documents || documents.length === 0) {
    return;
  }
  await Promise.all(
    documents.map((document) => {
      if (document.url) {
        return deleteDsaPartnerDocument(document.url);
      }
      return Promise.resolve();
    }),
  );
};

export const deleteDsaVisitingCardPhoto = async (
  photoUrl?: string,
): Promise<void> => {
  if (photoUrl) {
    await deleteDsaPartnerDocument(photoUrl);
  }
};

const timestampToDate = (timestamp: unknown): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (typeof timestamp === "string") {
    return new Date(timestamp);
  }
  return new Date();
};

const DSA_PARTNER_TYPES: ReadonlySet<DsaPartnerType> = new Set([
  "dsa",
  "hlc",
  "banker",
  "financial_advisor",
  "others",
]);

const toDsaPartnerType = (type: unknown): DsaPartnerType => {
  return typeof type === "string" && DSA_PARTNER_TYPES.has(type as DsaPartnerType)
    ? (type as DsaPartnerType)
    : "dsa";
};

const convertFirestoreDsaPartner = (
  docData: Record<string, unknown>,
): DsaPartner => {
  return {
    ...docData,
    type: toDsaPartnerType(docData.type),
    createdAt: timestampToDate(docData.createdAt),
    updatedAt: docData.updatedAt
      ? timestampToDate(docData.updatedAt)
      : undefined,
    lastVisitDate: docData.lastVisitDate
      ? timestampToDate(docData.lastVisitDate)
      : undefined,
    lastFollowUpDate: docData.lastFollowUpDate
      ? timestampToDate(docData.lastFollowUpDate)
      : undefined,
    deactivatedAt: docData.deactivatedAt
      ? timestampToDate(docData.deactivatedAt)
      : undefined,
  } as DsaPartner;
};

export const subscribeToDsaPartners = (
  userId: string,
  callback: (partners: DsaPartner[]) => void,
): (() => void) => {
  const createdByQuery = query(
    dsaPartnersCollection,
    where("createdBy", "==", userId),
    orderBy("createdAt", "desc"),
  );

  const sharedWithQuery = query(
    dsaPartnersCollection,
    where("sharedWith", "array-contains", userId),
    orderBy("createdAt", "desc"),
  );

  // Queries without orderBy to use as a fallback while composite indexes build
  const createdByQueryNoOrder = query(
    dsaPartnersCollection,
    where("createdBy", "==", userId),
  );
  const sharedWithQueryNoOrder = query(
    dsaPartnersCollection,
    where("sharedWith", "array-contains", userId),
  );

  let createdPartners: DsaPartner[] = [];
  let sharedPartners: DsaPartner[] = [];

  const updatePartners = () => {
    const partnersMap = new Map<string, DsaPartner>();
    [...createdPartners, ...sharedPartners].forEach((partner) => {
      partnersMap.set(partner.id, partner);
    });
    const uniquePartners = Array.from(partnersMap.values());
    uniquePartners.sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
    );
    callback(uniquePartners);
  };

  const unsubscribeCreated = onSnapshot(
    createdByQuery,
    (snapshot) => {
      createdPartners = snapshot.docs.map((doc) => {
        const data = doc.data();
        return convertFirestoreDsaPartner({ ...data, id: doc.id });
      });
      updatePartners();
    },
    async (error) => {
      console.error("Error fetching created DSA partners:", error);
      // If the error is due to an index being built, fall back to a no-order query
      const msg = error && (error.message || "");
      if (
        msg.includes("requires an index") ||
        msg.includes("index is currently building")
      ) {
        try {
          const snap = await getDocs(createdByQueryNoOrder);
          createdPartners = snap.docs.map((doc) =>
            convertFirestoreDsaPartner({ ...doc.data(), id: doc.id }),
          );
          updatePartners();
          return;
        } catch (e) {
          console.error("Fallback fetch for created DSA partners failed:", e);
        }
      }
      createdPartners = [];
      updatePartners();
    },
  );

  const unsubscribeShared = onSnapshot(
    sharedWithQuery,
    (snapshot) => {
      sharedPartners = snapshot.docs.map((doc) => {
        const data = doc.data();
        return convertFirestoreDsaPartner({ ...data, id: doc.id });
      });
      updatePartners();
    },
    async (error) => {
      console.error("Error fetching shared DSA partners:", error);
      const msg = error && (error.message || "");
      if (
        msg.includes("requires an index") ||
        msg.includes("index is currently building")
      ) {
        try {
          const snap = await getDocs(sharedWithQueryNoOrder);
          sharedPartners = snap.docs.map((doc) =>
            convertFirestoreDsaPartner({ ...doc.data(), id: doc.id }),
          );
          updatePartners();
          return;
        } catch (e) {
          console.error("Fallback fetch for shared DSA partners failed:", e);
        }
      }
      sharedPartners = [];
      updatePartners();
    },
  );

  return () => {
    unsubscribeCreated();
    unsubscribeShared();
  };
};

export const addDsaPartner = async (
  partner: Omit<DsaPartner, "id">,
  userId: string,
): Promise<DsaPartner> => {
  try {
    if (partner.whatsappNumber) {
      const dupQ = query(
        dsaPartnersCollection,
        where("createdBy", "==", userId),
        where("whatsappNumber", "==", partner.whatsappNumber),
      );
      const dupSnap = await getDocs(dupQ);
      if (!dupSnap.empty) {
        throw new Error(
          "DUPLICATE_DSA: A DSA partner with this WhatsApp number already exists in your list.",
        );
      }
    }

    const now = Timestamp.now();
    const partnerData: Record<string, unknown> = {
      ...partner,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    };

    Object.keys(partnerData).forEach((key) => {
      if (partnerData[key] === undefined) {
        delete partnerData[key];
      }
    });

    const docRef = await addDoc(dsaPartnersCollection, partnerData);

    return {
      ...partner,
      id: docRef.id,
      createdBy: userId,
      createdAt: now.toDate(),
      updatedAt: now.toDate(),
    };
  } catch (error) {
    console.error("Error adding DSA partner:", error);
    throw new Error(
      `Failed to add DSA partner: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
};

export const updateDsaPartner = async (
  partnerId: string,
  updates: Partial<DsaPartner>,
): Promise<void> => {
  try {
    const partnerRef = doc(db, "dsaPartners", partnerId);
    const updateData: Record<string, unknown> = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    Object.keys(updateData).forEach((key) => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    if ("sharedWith" in updates && updates.sharedWith === undefined) {
      updateData.sharedWith = deleteField();
    }
    if ("deactivatedAt" in updates && updates.deactivatedAt === undefined) {
      updateData.deactivatedAt = deleteField();
    }
    if (
      "deactivationReason" in updates &&
      updates.deactivationReason === undefined
    ) {
      updateData.deactivationReason = deleteField();
    }

    await updateDoc(partnerRef, updateData);
  } catch (error) {
    console.error("Error updating DSA partner:", error);
    throw new Error(
      `Failed to update DSA partner: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
};

export const deleteDsaPartner = async (
  partnerId: string,
  partner: DsaPartner,
): Promise<void> => {
  try {
    if (partner.documents && partner.documents.length > 0) {
      await deleteDsaPartnerDocuments(partner.documents);
    }

    if (partner.visitingCardPhoto) {
      await deleteDsaVisitingCardPhoto(partner.visitingCardPhoto);
    }

    const partnerRef = doc(db, "dsaPartners", partnerId);
    await deleteDoc(partnerRef);
  } catch (error) {
    console.error("Error deleting DSA partner:", error);
    throw new Error(
      `Failed to delete DSA partner: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
};

export const toggleDsaPartnerFavorite = async (
  partnerId: string,
  isFavorite: boolean,
): Promise<void> => {
  await updateDsaPartner(partnerId, { isFavorite });
};
